<script lang="ts" setup>
import { ref, computed, reactive, provide, onUnmounted, onMounted } from 'vue';
import { useSitemap } from '@/plugins/sitemap';
import { useRoute, useRouter } from 'vue-router';
import { useAlerts } from '@/plugins/alerts';
import { useLocalization } from '@/plugins/localization';
import { useEvents } from '@/plugins/events';
import { usePermissions } from '@/plugins/permissions';
import Pager from '@/helpers/Pager';
import { Resource } from '@/helpers/Interfaces';
import { entries } from 'lodash';
import { ProcessFlow } from '@/modules/low-code/inc/ProcessFlow';
import { ListItemModel } from '@/modules/logito/staff/services/PeopleService';
import { DetailsContract } from '@/components/dynamic-details/blueprints/details';
import { Resource as FileResource } from '@/modules/core/files/services/StorageService';
import { Column, ColumnDefinitionModel, IMetaModel } from '@/modules/core/common/services/GridService';
import ShareOneDriveService, { Attachment } from '@/modules/low-code/services/ShareOneDriveService';
import RegisterTasksService from '@/modules/logito/office/services/RegisterTasksService';
import { FluentDetailsBuilder } from '@/components/dynamic-details';
import ModulesService from '@/modules/low-code/services/ModulesService';

import StaffModal from '@/modules/logito/staff/components/StaffModal.vue';
import ShareOneDriveModal from '@/modules/low-code/components/modals/ShareOneDriveModal.vue';
import ProcessDynamicModal from '@/modules/low-code/components/modals/process/BPMNDynamicModal.vue';
import ProcessFinishDescriptionModal from '@/modules/low-code/components/modals/process/BPMNFinishDescriptionModal.vue';
import ProcessSignDocumentSimplySignModal from '@/modules/low-code/components/modals/process/BPMNSignDocumentSimplySignModal.vue';
import ProcessSignDocumentMSzafirModal from '@/modules/low-code/components/modals/process/BPMNSignDocumentMSzafirModal.vue';
import ProcessAcceptModal from '@/modules/low-code/components/modals/process/BPMNAcceptModal.vue';
import ProcessReturnModal from '@/modules/low-code/components/modals/process/BPMNReturnModal.vue';
import ProcessOwnerModal from '@/modules/low-code/components/modals/process/BPMNOwnerModal.vue';
import ProcessResumeModal from '@/modules/low-code/components/modals/process/BPMNResumeModal.vue';
import ProcessUnblockDocumentModal from '@/modules/low-code/components/modals/process/BPMNUnblockDocumentModal.vue';
import ProcessSetTargetsModal from '@/modules/low-code/components/modals/process/SetTargetsModal.vue';
import ChangeProcessModal from '@/modules/low-code/components/modals/process/ChangeProcessModal.vue';
import RemoveParticipantsModal from '@/modules/low-code/components/modals/process/RemoveParticipantsModal.vue';
import MarkShareAsReadModal from '@/modules/low-code/components/modals/MarkShareAsReadModal.vue';
import ProcessSignDocumentSzafirWebModuleModal from '@/modules/low-code/components/modals/process/BPMNSignDocumentSzafirWebModuleModal.vue';
import ChangeDocumentDataModal from '@/modules/low-code/components/modals/process/ChangeDocumentDataModal.vue';
import ChangeModuleModal from '@/modules/logito/office/components/ChangeModuleModal.vue';
import IdeoSpinner from '@/components/ideo/spinner/IdeoSpinner.vue';
import FavoriteStar from '@/components/common/FavoriteStar.vue';
import axios from 'axios';

const props = defineProps({
  "licence": null,
  "publicId": null
});

const { $sitemap } = useSitemap();
const { $permissions } = usePermissions();
const { $events } = useEvents();
const { $alert } = useAlerts();
const { $t, $i18n } = useLocalization();
const router = useRouter();
const route = useRoute();

const staffModal = ref<InstanceType<typeof StaffModal>>(null);
const activeUser = ref<ListItemModel>(null);

const definition = ref<DetailsContract>(null);

const builder = reactive(
    FluentDetailsBuilder.setup(false, true)
        .blueprint(
            () => definition.value,
            () => 0
        )
        .make()
);

const schema = ref<ColumnDefinitionModel>(null);
const actions = ref<Record<string, IMetaModel>>(null);
const oneDriveSharedDocuments = ref<Array<Attachment>>([]);
const filePreview = ref<FileResource>(null);
const detailsData = ref<Resource<Record<string, any>>>(null);
const permissions = ref<Record<string, boolean>>({ canEdit: true });
const isAwaitingTasks = ref<boolean>(null);
const isBlocked = ref<boolean>(false);
const awaitingTimeoutId = ref<NodeJS.Timeout>(null);
const isAdmin = ref<boolean>(false);

const endpoint = computed(() => `${isOffice.value ? 'office' : props.licence}/${props.publicId}`);
const isOffice = computed(() => route.query.office === 'true');
// const isEditMode = computed(() => detailsData.value && !!detailsData.value.result.publicId);
const processFlow = computed(() => detailsData.value?.result?.processFlow as ProcessFlow);

const detailsArray = computed<[string, Column][]>(() =>
{
    const f = entries(schema.value);

    return f
        .filter(
            (column) => column[1].visibility && column[0] !== 'id' && column[0] !== 'publicId' && column[0] !== 'userId'
        )
        .sort((a, b) =>
        {
            if (b[1].order < a[1].order) return 1;

            return -1;
        });
});

provide('schema', schema);
provide('actions', actions);
provide('oneDriveSharedDocuments', oneDriveSharedDocuments);
provide('file-preview', filePreview);
provide('values', detailsData);
provide('permissions', permissions);
provide('configs', detailsArray);
provide('process-flow', processFlow);
provide('isAdmin', isAdmin);

const setPermissions = (_permissions: Record<string, IMetaModel>) =>
{
    actions.value = { ..._permissions };
    permissions.value.canEdit = _permissions.edit?.can || false;
    permissions.value.canDescribe =
        Object.values(_permissions).find((p) => p.frontAction == 'InvoiceDescriptionModel')?.can ?? false;
};

const deleteDocument = async (document: any) =>
{
    try
    {
        await RegisterTasksService.deleteDocument(document.publicId);
        $alert.success($t('[[[Dokument został usunięty.]]]'));
        router.push({ name: 'office-registerTasks' });
    }
    catch
    {
        $alert.error($t('[[[Wystąpił błąd podczas usuwania dokumentu.]]]'));
    }
};

const changeModule = (document: any) =>
{
    $events.$emit('change-module-modal', document);
};

const reload = () =>
{
    fetchInfo();
    fetchSharedDocuments();
    loadData();
    fetchHistory();
};

const setEvents = () =>
{
    // TODO: In the feature, we should limit the number of global events
    $events.$on('refetchData', () =>
    {
        reload();
    });

    // TODO: In the feature, we should limit the number of global events
    $events.$on('refetchDataDetails', () =>
    {
        fetchSharedDocuments();
    });

    // TODO: In the feature, we should limit the number of global events
    $events.$on('fetchUser', async ({ id }: { id: string }) =>
    {
        const canDisplay = await $permissions.all(['Ideo.Logito.Accounts.Staff.InfoStaffPermissions@Access']);

        if (canDisplay) fetchUser(id);
    });
};

const fetchHistory = () =>
{
    $events.$emit('refetchHistory');
};

const fetchUser = async (id: string) =>
{
    try
    {
        activeUser.value = await ModulesService.fetchUser(id);
        staffModal.value?.openModal();
    }
    catch
    {
        $alert.danger($t('[[[Błąd podczas pobierania użytkownika]]]'));
    }
};

const fetchSchema = async () =>
{
    try
    {
        return await ModulesService.fetchDetailsSchema(props.licence, props.publicId);
    }
    catch (error)
    {
        $alert.danger($t('[[[Błąd podczas pobierania schematu]]]'));

        throw error;
    }
};

const fetchData = async () =>
{
    try
    {
        return await ModulesService.fetchDetailsData(props.licence, props.publicId);
    }
    catch (error)
    {
        $alert.danger($t('[[[Błąd podczas pobierania danych]]]'));

        throw error;
    }
};

const fetchInfo = async () =>
{
    ModulesService.markAsSeen(props.licence, props.publicId);

    const [schemaData, data] = await Promise.all([fetchSchema(), fetchData(), fetchIsAdmin()]);

    schema.value = schemaData;
    detailsData.value = data;

    const [entry] = Object.entries(schemaData).filter((entry) => entry[1].type.baseType === 'ResourceConfidential');

    if (entry)
    {
        const [name] = entry;

        filePreview.value = detailsData.value.result[name];
    }
};

const fetchIsAdmin = async () =>
{
    const moduleId = await ModulesService.getModuleId(props.licence);

    isAdmin.value = await $permissions.all([`Ideo.Logito.LowCode.Modules.Permissions.ModulePermissions:${moduleId}@Admin`]);
};

const fetchSharedDocuments = async () =>
{
    try
    {
        const result = await ShareOneDriveService.getSharedDocuments(props.licence, props.publicId, new Pager(1, 999));

        oneDriveSharedDocuments.value = result.items.map((item) => item?.result);
    }
    catch (ex: any)
    {
        if (ex.code === 400) $alert.warning($t('[[[Błąd podczas pobierania udostępnionych dokumentów]]]'));
    }
};

const loadData = async () =>
{
    try
    {
        const result = await ModulesService.fetchDefinition(props.licence, props.publicId);

        definition.value = result.definition ?? {};
        definition.value.kind = 'details';
        definition.value.root = true;
        builder.update();

        isAwaitingTasks.value = result.isAwaitingTasks;
        isBlocked.value = result.isBlocked;

        if (isAwaitingTasks.value === true)
        {
            awaitingTimeoutId.value = setTimeout(() => fetchAwaitingStatus(), 2000);
        }
    }
    catch (ex: any)
    {
        if (ex.code === 400) $alert.warning($t('[[[Błąd podczas pobierania szczegółów]]]'));
    }
};

const fetchAwaitingStatus = async () =>
{
    const isAwaiting = await ModulesService.fetchDocumentAwaitingStatus(props.licence, props.publicId);

    isAwaitingTasks.value = isAwaiting;

    if (isAwaiting)
    {
        awaitingTimeoutId.value = setTimeout(() => fetchAwaitingStatus(), 2000);
    }
    else
    {
        await fetchInfo();
        isBlocked.value = await ModulesService.fetchDocumentBlockedStatus(props.licence, props.publicId);
    }
};

const updateFavorite = (isFavorite: boolean) =>
{
    if (detailsData.value?.result)
    {
        detailsData.value.result.isFavorite = isFavorite;
    }
};

const getDocumentEndpoint = (documentId: string) => `simple-fetch/${props.licence}/${props.publicId}/${documentId}`;

onUnmounted(() =>
{
    if (awaitingTimeoutId.value)
        clearTimeout(awaitingTimeoutId.value);
});

const init = async () =>
{
    await fetchInfo();
    await fetchSharedDocuments();
    await setEvents();
    await loadData();

    $sitemap.extraCrumb = $i18n.currentTranslation(definition.value.title);
};

init();
</script>

<template>
    <DataCard>
        <template #toolbar>
            <FetchData v-if="isAwaitingTasks === false" v-slot="{ actions }" :endpoint="endpoint" @meta-changed="setPermissions">
                <IdeoPanel :column="false" :stretch="false" :start="desktop">
                    <template #start>
                        <ActionBar :responsive="true">
                            <BackButton toolbar />
                            <DetailsActions
                                :actions="actions"
                                :except="[
                                    'document-notifications-create',
                                    'canManageParticipants',
                                    'canUploadOneDrive',
                                    'canSetPrivate',
                                ]"
                                :url="isOffice ? 'office' : licence"
                                :is-office="isOffice"
                                :public-id="publicId"
                            />
                            <ActionButton
                                v-show="detailsData && isOffice"
                                :text="$t('[[[Zmień moduł]]]')"
                                toolbar
                                icon="fas fa-exchange-alt"
                                variant="warning"
                                @click="changeModule(detailsData.result)"
                            />
                            <ActionButton
                                v-show="detailsData && isOffice"
                                :text="$t('[[[Usuń]]]')"
                                toolbar
                                icon="fas fa-times"
                                variant="danger"
                                :confirm="$t('[[[Czy na pewno chcesz usunąć ten dokument?]]]')"
                                @click="deleteDocument(detailsData.result)"
                            />
                        </ActionBar>
                    </template>
                    <template #end>
                        <div class="toolbar-actions">
                            <ExportManager
                                :document="{
                                    endpoint: getDocumentEndpoint,
                                    actions,
                                }"
                            />
                            <DetailsActions
                                v-if="Object.keys(actions).length"
                                :actions="actions"
                                :except="[
                                    'document-notifications-create',
                                    'canManageParticipants',
                                    'canUploadOneDrive',
                                    'canSetPrivate',
                                ]"
                                :url="isOffice ? 'office' : licence"
                                :public-id="publicId"
                                :admin="true"
                            />
                            <FavoriteStar
                                v-if="detailsData?.result"
                                :public-id="publicId"
                                :is-favorite="detailsData.result.isFavorite"
                                @update:favorite="updateFavorite"
                            />
                        </div>
                    </template>
                </IdeoPanel>
            </FetchData>
            <template v-if="isAwaitingTasks === true">
                <div class="row">
                    <div class="col-md-2 w-auto">
                        <IdeoSpinner />
                    </div>
                    <div class="col-md-10 h4 w-auto">
                        {{ $t('[[[Trwa przetwarzanie dokumentu, przyciski akcji będą dostępne po zakończeniu]]]') }}
                    </div>
                </div>
            </template>
            <template v-if="isBlocked === true">
                <div class="row">
                    <div class="col-12">
                        <div
                            class="alert alert-danger mt-2 mb-0 d-flex align-items-start gap-2"
                            role="alert"
                            aria-live="polite"
                        >
                            <i class="fas fa-circle-exclamation flex-shrink-0 mt-1" aria-hidden="true" />
                            <div class="flex-grow-1">
                                <div class="fw-bold lh-sm">
                                    {{ $t('[[[Dokument zablokowany]]]') }}
                                </div>
                                <div class="mt-1 lh-sm text-break">
                                    {{ $t('[[[Wystąpił błąd podczas przetwarzania. Skontaktuj się z administratorem systemu, aby odblokować dokument.]]]') }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </template>
        <template #default>
            <div class="row">
                <div class="col-md-12">
                    <DynamicDetailsDetailsBlueprint :builder="builder" />
                </div>
            </div>
        </template>
        <template #modal>
            <StaffModal ref="staffModal" :user="activeUser" @close="activeUser = null" />
            <ShareOneDriveModal :licence="licence" :public-id="publicId" @reload="reload" />
            <ChangeProcessModal @reload="reload" />
            <RemoveParticipantsModal @reload="reload" />
            <MarkShareAsReadModal @reload="reload" />
            <ProcessAcceptModal @reload="reload" />
            <ProcessOwnerModal @reload="reload" />
            <ProcessReturnModal @reload="reload" />
            <ProcessDynamicModal :level="1" @reload="reload" />
            <ProcessFinishDescriptionModal @reload="reload" />
            <ProcessSignDocumentSimplySignModal :module="isOffice ? 'office' : licence" @reload="reload" />
            <ProcessSignDocumentSzafirWebModuleModal @reload="reload" />
            <ProcessSignDocumentMSzafirModal />
            <ProcessResumeModal @reload="reload" />
            <ProcessSetTargetsModal @reload="reload" />
            <ChangeDocumentDataModal @reload="reload" />
            <ChangeModuleModal @reload="reload" />
            <ProcessUnblockDocumentModal @reload="reload" />
        </template>
    </DataCard>
</template>
